import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { compose } from 'redux';
import { injectIntl, FormattedMessage } from '../../util/reactIntl';
import { PortfolioWorkCard, ProfileSettingsContainer as Container } from '../../components';
import { FaSave } from 'react-icons/fa';
import { GoPin } from 'react-icons/go';

import css from './PortfolioWorksContainer.css';

// Constants
const INITIAL_DISPLAY_COUNT = 3;
const MAX_FEATURED_WORKS = 3;

// Custom hook for portfolio data
const usePortfolioData = (portfolioWorks, featuredPortfolioOrder) => {
  return useMemo(() => {
    const hasWorks = portfolioWorks && portfolioWorks.length > 0;

    // Sort portfolio works: featured first (by order), then the rest
    let sortedWorks = [];
    if (hasWorks && featuredPortfolioOrder && featuredPortfolioOrder.length > 0) {
      const featuredWorks = [];
      const remainingWorks = [];

      portfolioWorks.forEach(work => {
        const workId = work?.id?.uuid || work?.id;
        const orderEntry = featuredPortfolioOrder.find(entry => entry.id === workId);
        if (orderEntry) {
          featuredWorks.push({ work, order: orderEntry.order });
        } else {
          remainingWorks.push(work);
        }
      });

      // Sort featured works by order
      featuredWorks.sort((a, b) => a.order - b.order);
      sortedWorks = [...featuredWorks.map(fw => fw.work), ...remainingWorks];
    } else {
      sortedWorks = portfolioWorks || [];
    }

    const shouldShowButton = hasWorks && sortedWorks.length > INITIAL_DISPLAY_COUNT;

    return {
      hasWorks,
      shouldShowButton,
      totalCount: sortedWorks?.length || 0,
      sortedWorks,
    };
  }, [portfolioWorks, featuredPortfolioOrder]);
};

// Portfolio work list component
const PortfolioWorkList = ({
  portfolioWorks,
  showAll,
  orderMode,
  selectedOrder,
  onSelectOrder,
  isEditable,
}) => {
  const displayCount = showAll ? portfolioWorks.length : INITIAL_DISPLAY_COUNT;
  const worksToDisplay = portfolioWorks.slice(0, displayCount);

  // Check if all slots are filled
  const allSlotsFilled = Object.keys(selectedOrder).length >= MAX_FEATURED_WORKS;

  return (
    <div className={css.portfolioWorkList}>
      {worksToDisplay.map((work, idx) => (
        <PortfolioWorkCard
          key={work?.id?.uuid || work?.id || idx}
          portfolioWork={work}
          orderMode={orderMode}
          selectedOrder={selectedOrder}
          onSelectOrder={onSelectOrder}
          allSlotsFilled={allSlotsFilled}
          isEditable={isEditable}
        />
      ))}
    </div>
  );
};

// Show all button component
const ShowAllButton = ({ totalCount, onToggleShowAll }) => (
  <div className={css.toggleShowMore} onClick={onToggleShowAll}>
    <FormattedMessage id="PortfolioWorkContainer.showAll" />
    <div className={css.showAllCount}>&nbsp;({totalCount})</div>
  </div>
);

const PortfolioWorksContainerComponent = ({
  portfolioWorks,
  button,
  intl,
  currentUser,
  onUpdateProfile,
  showOrderButton = false,
  isEditable = false,
}) => {
  const [showAll, setShowAll] = useState(false);
  const [orderMode, setOrderMode] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState({});

  const featuredPortfolioOrder =
    currentUser?.attributes?.profile?.publicData?.featuredPortfolioOrder || [];

  const { hasWorks, shouldShowButton, totalCount, sortedWorks } = usePortfolioData(
    portfolioWorks,
    featuredPortfolioOrder
  );

  // Initialize selected order from publicData
  useEffect(() => {
    if (featuredPortfolioOrder && featuredPortfolioOrder.length > 0) {
      const orderMap = {};
      featuredPortfolioOrder.forEach(entry => {
        orderMap[entry.id] = entry.order;
      });
      setSelectedOrder(orderMap);
    }
  }, [featuredPortfolioOrder]);

  // Event handlers
  const toggleShowAll = useCallback(() => setShowAll(prev => !prev), []);

  const toggleOrderMode = useCallback(() => {
    setOrderMode(prev => {
      const newOrderMode = !prev;
      // When entering order mode, show all works
      if (newOrderMode) {
        setShowAll(true);

        // Clean up featuredPortfolioOrder: remove works that are no longer published or don't exist
        if (featuredPortfolioOrder && featuredPortfolioOrder.length > 0 && portfolioWorks) {
          const publishedWorkIds = new Set(
            portfolioWorks
              .filter(work => work?.attributes?.state === 'published')
              .map(work => work?.id?.uuid || work?.id)
          );

          const cleanedOrder = featuredPortfolioOrder.filter(entry =>
            publishedWorkIds.has(entry.id)
          );

          // If some entries were removed, update the profile
          if (
            cleanedOrder.length !== featuredPortfolioOrder.length &&
            onUpdateProfile &&
            currentUser
          ) {
            onUpdateProfile({
              publicData: {
                ...currentUser.attributes.profile.publicData,
                featuredPortfolioOrder: cleanedOrder,
              },
            });
          }
        }
      }
      return newOrderMode;
    });
  }, [featuredPortfolioOrder, portfolioWorks, onUpdateProfile, currentUser]);

  const handleSelectOrder = useCallback(workId => {
    setSelectedOrder(prev => {
      const newOrder = { ...prev };

      // If this work already has an order, deselect it and reorder remaining
      if (newOrder[workId]) {
        const removedOrder = newOrder[workId];
        delete newOrder[workId];

        // Reorder remaining items: shift down items that were after the removed one
        Object.keys(newOrder).forEach(id => {
          if (newOrder[id] > removedOrder) {
            newOrder[id] = newOrder[id] - 1;
          }
        });

        return newOrder;
      }

      // Find the next available order number (1, 2, or 3)
      const usedOrders = Object.values(newOrder);
      let nextOrder = null;

      for (let i = 1; i <= MAX_FEATURED_WORKS; i++) {
        if (!usedOrders.includes(i)) {
          nextOrder = i;
          break;
        }
      }

      // Only assign if we have an available slot
      if (nextOrder) {
        newOrder[workId] = nextOrder;
      }

      return newOrder;
    });
  }, []);

  const handleSaveOrder = useCallback(() => {
    // Convert selectedOrder object to array format for publicData
    const featuredPortfolioOrder = Object.entries(selectedOrder)
      .map(([id, order]) => ({ id, order }))
      .filter(entry => entry.order >= 1 && entry.order <= MAX_FEATURED_WORKS);

    // Update user profile with new order
    if (onUpdateProfile && currentUser) {
      onUpdateProfile({
        publicData: {
          ...currentUser.attributes.profile.publicData,
          featuredPortfolioOrder,
        },
      });
    }

    setOrderMode(false);
    setShowAll(false);
  }, [selectedOrder, onUpdateProfile, currentUser]);

  // Early return if no portfolio works and no button
  if (!hasWorks && !button) {
    return null;
  }

  // Order mode button
  const orderButton = hasWorks && showOrderButton && (
    <div
      className={orderMode ? css.orderButtonContainerOpen : css.orderButtonContainer}
      onClick={orderMode ? handleSaveOrder : toggleOrderMode}
      title={intl.formatMessage({
        id: orderMode
          ? 'PortfolioWorkContainer.saveOrderTitle'
          : 'PortfolioWorkContainer.orderButtonTitle',
      })}
      style={{ cursor: 'pointer' }}
    >
      {orderMode && (
        <div className={css.orderModeBar}>
          <p className={css.orderModeText}>
            <FormattedMessage id="PortfolioWorkContainer.orderModeText" />
          </p>
        </div>
      )}
      <button type="button" className={orderMode ? css.orderButtonSave : css.orderButton}>
        {orderMode ? <FaSave /> : <GoPin />}
      </button>
    </div>
  );

  // Combine buttons
  const combinedButtons = (
    <div className={css.buttonGroup}>
      {orderButton}
      {button}
    </div>
  );

  return (
    <Container
      id="portfolio"
      heading={<FormattedMessage id="PortfolioWorkContainer.heading" />}
      button={combinedButtons}
    >
      {hasWorks && (
        <>
          <PortfolioWorkList
            portfolioWorks={sortedWorks}
            showAll={showAll}
            orderMode={orderMode}
            selectedOrder={selectedOrder}
            onSelectOrder={handleSelectOrder}
            isEditable={isEditable}
          />
          {shouldShowButton && !showAll && (
            <ShowAllButton totalCount={totalCount} onToggleShowAll={toggleShowAll} />
          )}
        </>
      )}
    </Container>
  );
};

const PortfolioWorksContainer = compose(injectIntl)(PortfolioWorksContainerComponent);

export default PortfolioWorksContainer;
